
import { Tables } from "@/integrations/supabase/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getAvatarUrl } from "@/utils/storageUtils";
import { useState, useEffect, useRef } from "react";


type VacationRequest = Tables<"vacation_requests"> & {
  profiles: Pick<Tables<"profiles">, "full_name" | "avatar_url">;
};

interface WelcomeSectionProps {
  profile: Tables<"profiles">;
  remainingDays: number;
  nextVacation: Tables<"vacation_requests"> | null;
  vacationDuration: number;
  currentlyOnVacation: VacationRequest[];
}

const AnimatedNumber = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const animationRef = useRef<number>();

  useEffect(() => {
    const duration = 1200;
    const startTime = performance.now();
    const startValue = 0;
    const endValue = value;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (endValue - startValue) * eased);
      setDisplayValue(current);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [value]);

  const formatted = displayValue < 10 ? `0${displayValue}` : `${displayValue}`;

  return (
    <span className="inline-block tabular-nums">{formatted}</span>
  );
};

export const WelcomeSection = ({
  profile,
  remainingDays,
  nextVacation,
  vacationDuration,
  currentlyOnVacation,
}: WelcomeSectionProps) => {
  const currentDate = new Date();
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  
  useEffect(() => {
    if (profile?.avatar_url) {
      const url = getAvatarUrl(profile.avatar_url);
      setAvatarUrl(url);
      setImageError(false);
    }
  }, [profile?.avatar_url]);

  const handleAvatarError = () => {
    setImageError(true);
  };

  return (
    <div className="w-full bg-gradient-to-r from-blue-500 to-green-500 rounded-lg p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Left: Avatar + Name + Date */}
        <div className="flex items-center space-x-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar className="h-16 w-16 border-2 border-white/30 transition-transform hover:scale-110 cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D3E4FD] to-[#F1F0FB] rounded-full" />
                  {avatarUrl && !imageError ? (
                    <AvatarImage 
                      src={avatarUrl}
                      alt={profile?.full_name || 'User avatar'}
                      className="object-cover relative z-10"
                      onError={handleAvatarError}
                    />
                  ) : (
                    <AvatarFallback className="text-xl font-semibold bg-transparent text-gray-700 relative z-10">
                      {profile?.full_name?.split(' ').map(n => n[0]).join('') || '??'}
                    </AvatarFallback>
                  )}
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to update your profile picture</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="text-white">
            <h1 className="text-2xl font-bold">{profile?.full_name}</h1>
            <p className="text-sm opacity-80">{format(currentDate, 'MMMM d, yyyy')}</p>
          </div>
        </div>

        {/* Right: Days left stat */}
        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/30">
          <div className="text-white text-center">
            <div className="text-3xl font-extrabold leading-none tracking-tight">
              <AnimatedNumber value={remainingDays} />
            </div>
            <p className="text-xs font-medium opacity-80 mt-0.5">Days left</p>
          </div>
        </div>
      </div>
    </div>
  );
};
